import React, { useState, useMemo, useEffect } from 'react';
import styled from '@emotion/styled';
import { connect } from 'react-redux';
import { useJoin } from '../../contexts/JoinContext';
import JoinActionButton from './JoinActionButton';
import JoinSkipButton from './JoinSkipButton';
import { DashlineSeparator, FlexContainer, SeparatorLine, VerticalSeparator } from './styledElements';
import JoinOfferAmountInput from './JoinOfferAmountInput';
import FirstDateOfferModal from '../common/modals/FirstDateOfferModal';
import { getDefaultOfferAmount } from '../../utils/offers';
import breakpoints from '../../theme/material/breakpoints';
import { RootState } from '../../store';
import { defaultOfferAmount } from '../../common';

const StyledContainer = styled('div')({
  marginTop: 'unset',
  [breakpoints.down('sm')]: {
    marginTop: 48,
  },
});

interface Props {
  profile: any;
}

const JoinOpeningOffer: React.FC<Props> = ({ profile }: Props) => {
  const { currentFormData, nextStep, setCurrentFormData, prevStep, handleShowSkipModal, isMandatoryField } = useJoin();
  const [shouldDisableNextButton, setShouldDisableNextButton] = useState(false);

  const currencySymbols = useMemo(() => {
    let currencyInitial;
    let currencyLabelUnicode;

    if (profile?.currency) {
      currencyInitial = profile.currency.data.initial;
      currencyLabelUnicode = profile.currency.data.label_unicode;
    }

    return {
      currencyInitial,
      currencyLabel: String.fromCharCode(currencyLabelUnicode),
    };
  }, [profile]);

  const updateOfferPrice = (offerPrice: string, hasError: boolean) => {
    setCurrentFormData({ default_offer_amount: offerPrice });
    setShouldDisableNextButton(hasError);
  };

  const currencyPrefix = useMemo(() => {
    return `${currencySymbols?.currencyInitial ?? ''}${currencySymbols?.currencyLabel ?? ''}`;
  }, [currencySymbols]);

  const onSkip = () => {
    setCurrentFormData({ default_offer_amount: null });
    nextStep(true);
  };

  const defaultMinOffer = defaultOfferAmount.minValue;

  useEffect(() => {
    if (currentFormData.default_offer_amount === null) {
      setCurrentFormData({ default_offer_amount: getDefaultOfferAmount() });
    }
  }, []);

  return (
    <StyledContainer>
      <JoinOfferAmountInput
        offerPrice={currentFormData.default_offer_amount ?? ''}
        updateOfferPrice={updateOfferPrice}
        hasError={shouldDisableNextButton}
        defaultMinOffer={defaultMinOffer}
        currencyPrefix={currencyPrefix}
        isMandatoryField={isMandatoryField()}
      />
      <FirstDateOfferModal
        linkStyle={{ textAlign: 'center' }}
        customStyle={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        defaultMinOffer={defaultMinOffer}
        currencyPrefix={currencyPrefix}
      />
      <DashlineSeparator style={{ marginTop: 26, marginBottom: 26 }} />
      <div style={{ textAlign: 'center' }}>
        <JoinActionButton
          label="Next"
          isDisabled={shouldDisableNextButton}
          onClick={() => {
            nextStep();
          }}
          customStyle={{ marginBottom: 10 }}
          isDefault={true}
        />

        <FlexContainer>
          <VerticalSeparator>
            <JoinSkipButton label="Back" onClick={() => prevStep()} />
            {!isMandatoryField() && (
              <>
                <SeparatorLine />
                <JoinSkipButton label="Skip" onClick={() => handleShowSkipModal(onSkip)} />
              </>
            )}
          </VerticalSeparator>
        </FlexContainer>
      </div>
    </StyledContainer>
  );
};

const mapDispatchToProps = () => {
  return {};
};

const mapStateToProps = (state: RootState) => {
  return {
    profile: state.profile,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(JoinOpeningOffer);
